import Button from "atoms/buttons/button";
import ChatRenameConversationModal from "./chat-rename-conversation-modal";
import ChatTitleInput from "./chat-title-input";
import ChatTooltipButton from "molecules/tooltips/chat-tooltip-button";
import ConversationRecord from "models/view-models/conversations/conversation-record";
import Icon from "atoms/icons/icon";
import MenuButton from "molecules/menu-button/menu-button";
import React, { useState } from "react";
import { IconSizes } from "atoms/constants/icon-sizes";
import { Icons } from "atoms/constants/icons";
import { useLocalization } from "utilities/hooks/use-localization";
import useBreakpoint, {
    BreakpointComparer,
} from "utilities/hooks/use-breakpoint";
import { Breakpoints } from "utilities/enumerations/breakpoints";

// -----------------------------------------------------------------------------------------
// #region Interfaces
// -----------------------------------------------------------------------------------------

interface ChatPastConversationsGroupProps {
    name: string;
    conversations: Omit<ConversationRecord, "messages">[];
    /** ID of the currently selected conversation - used to highlight the active conversation */
    selectedConversationId?: string;
    onConversationSelected?: (conversationId?: string) => void;
    onConversationRenamed: (
        conversation: Pick<ConversationRecord, "id" | "title">
    ) => void;

    onConversationDelete?: (conversationId: string) => void;
    onConversationPinToggle: (
        conversation: Pick<ConversationRecord, "id" | "isPinned">
    ) => void;
    isPinnedGroup?: boolean;
}

// #endregion Interfaces

// -----------------------------------------------------------------------------------------
// #region Constants
// -----------------------------------------------------------------------------------------

export const PAST_CONVO_GROUP_BASE_CLASS_NAME =
    "c-chat-modal-past-conversations-group";

// #endregion Constants

// -----------------------------------------------------------------------------------------
// #region Component
// -----------------------------------------------------------------------------------------

const ChatPastConversationsGroup: React.FC<ChatPastConversationsGroupProps> = ({
    name: groupName,
    conversations,
    selectedConversationId,
    onConversationSelected,
    onConversationRenamed,
    onConversationDelete,
    onConversationPinToggle,
    isPinnedGroup = false,
}) => {
    const { t } = useLocalization();
    const [editingConversationId, setEditingConversationId] = useState<
        string | null
    >(null);
    const isMobile = useBreakpoint(
        Breakpoints.Phone,
        BreakpointComparer.MaxWidth
    );

    const handleLoadConversation = (conversationId?: string) => {
        if (!conversationId) {
            return;
        }

        onConversationSelected?.(conversationId);
    };

    const handleDeleteConversation = (conversationId?: string) => {
        if (!conversationId) {
            return;
        }

        // delete the conversation
        onConversationDelete?.(conversationId);
    };

    const handlePinUnpinConversation = (
        conversation: Pick<ConversationRecord, "id" | "isPinned">
    ) => {
        if (!conversation.id) {
            return;
        }
        onConversationPinToggle({
            id: conversation.id,
            isPinned: !conversation.isPinned,
        });
    };

    const handleRenameConversation = (
        conversation: Pick<ConversationRecord, "id" | "title">
    ) => {
        if (!conversation.id || !conversation.title) {
            return;
        }

        onConversationRenamed(conversation);
    };

    const startEditing = (
        conversation: Pick<ConversationRecord, "id" | "title">
    ) => {
        setEditingConversationId(conversation.id ?? null);
    };

    const getGroupItemClassName = (conversationId?: string) => {
        const isSelected = selectedConversationId === conversationId;
        const selectedModifier = isSelected ? " --selected" : "";

        return `${PAST_CONVO_GROUP_BASE_CLASS_NAME}__group-item${selectedModifier}`;
    };

    const editingConversation = conversations.find(
        (c) => c.id === editingConversationId
    );

    const handleModalSave = (
        conversation: Pick<ConversationRecord, "id" | "title">
    ) => {
        handleRenameConversation(conversation);
        setEditingConversationId(null);
    };

    const handleModalClose = () => {
        setEditingConversationId(null);
    };

    return (
        <>
            {isMobile && editingConversation && (
                <ChatRenameConversationModal
                    conversation={editingConversation}
                    closeDialog={handleModalClose}
                    isVisible={true}
                    onSave={handleModalSave}
                />
            )}
            <div
                key={groupName}
                className={`${PAST_CONVO_GROUP_BASE_CLASS_NAME}`}>
                <div
                    className={`${PAST_CONVO_GROUP_BASE_CLASS_NAME}__group-name`}>
                    {groupName}
                    {isPinnedGroup && (
                        <Icon
                            size={IconSizes.Small}
                            cssClassName={`${PAST_CONVO_GROUP_BASE_CLASS_NAME}__pin-icon`}
                            type={Icons.PushPin}
                        />
                    )}
                </div>
                {conversations.map((conversation) => (
                    <div
                        key={conversation.id}
                        className={getGroupItemClassName(conversation.id)}>
                        {!isMobile &&
                        editingConversationId === conversation.id ? (
                            <ChatTitleInput
                                conversation={conversation}
                                onSubmit={(title) => {
                                    handleRenameConversation({
                                        id: conversation.id,
                                        title,
                                    });
                                    setEditingConversationId(null);
                                }}
                                onCancel={() => setEditingConversationId(null)}
                            />
                        ) : (
                            <ChatTooltipButton
                                conversation={conversation}
                                onLoadConversation={handleLoadConversation}
                            />
                        )}

                        <MenuButton
                            offlineAlertOptions={{
                                enabled: true,
                                tooltipHeader: t("offline-fallback-title"),
                                tooltipDescription: t(
                                    "chat-offline-description"
                                ),
                            }}
                            icon={Icons.MoreHorizontal}
                            iconSize={IconSizes.Medium}
                            cssClassName={`${PAST_CONVO_GROUP_BASE_CLASS_NAME}__menu-button${
                                isMobile &&
                                selectedConversationId === conversation.id
                                    ? " --always-visible"
                                    : ""
                            }`}>
                            <Button
                                onClick={() =>
                                    handlePinUnpinConversation(conversation)
                                }>
                                <Icon
                                    type={Icons.PushPin}
                                    size={IconSizes.Medium}></Icon>
                                {conversation.isPinned ? t("unpin") : t("pin")}
                            </Button>
                            <Button onClick={() => startEditing(conversation)}>
                                <Icon
                                    type={Icons.Pencil}
                                    size={IconSizes.Medium}></Icon>
                                {t("rename")}
                            </Button>
                            <Button
                                onClick={() =>
                                    handleDeleteConversation(conversation.id)
                                }>
                                <Icon
                                    type={Icons.Trashcan2}
                                    size={IconSizes.Medium}></Icon>
                                {t("delete")}
                            </Button>
                        </MenuButton>
                    </div>
                ))}
            </div>
        </>
    );
};

// #endregion Component

// -----------------------------------------------------------------------------------------
// #region Exports
// -----------------------------------------------------------------------------------------

export default ChatPastConversationsGroup;

// #endregion Exports
